import { useEffect, useMemo, useState } from "react";
import {
  FiArrowLeft,
  FiCheckCircle,
  FiClock,
  FiDownload,
  FiFileText,
  FiPrinter,
  FiShoppingBag,
  FiTruck,
  FiUser,
  FiXCircle,
} from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const dateTime = (value) =>
  value
    ? new Intl.DateTimeFormat("en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "—";

const dateOnly = (value) =>
  value
    ? new Intl.DateTimeFormat("en-IN", {
        dateStyle: "medium",
      }).format(new Date(value))
    : "—";

const statusLabels = {
  DRAFT: "Draft",
  CONFIRMED: "Confirmed",
  PARTIALLY_DELIVERED: "Partially Delivered",
  DELIVERED: "Delivered",
  CANCELLED: "Cancelled",
};

export default function SalesOrderDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { accessToken } = useAuth();

  const [activeTab, setActiveTab] = useState("overview");
  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [error, setError] = useState("");

  const loadOrder = async () => {
    try {
      setIsLoading(true);
      setError("");

      const response = await apiRequest(`/sales-orders/${id}`, {
        accessToken,
      });

      setOrder(response);
    } catch (requestError) {
      setError(requestError.message || "Unable to load sales order.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken && id) {
      loadOrder();
    }
  }, [accessToken, id]);

  const totalQuantity = useMemo(
    () =>
      (order?.items || []).reduce(
        (total, item) => total + Number(item.quantityOrdered || 0),
        0,
      ),
    [order],
  );

  const confirmOrder = async () => {
    try {
      setIsActionLoading(true);
      setError("");

      await apiRequest(`/sales-orders/${id}/confirm`, {
        method: "POST",
        accessToken,
      });

      await loadOrder();
    } catch (requestError) {
      setError(requestError.message || "Unable to confirm this sales order.");
    } finally {
      setIsActionLoading(false);
    }
  };

  const cancelOrder = async () => {
    const reason = window.prompt("Enter a cancellation reason:");

    if (!reason?.trim()) return;

    try {
      setIsActionLoading(true);
      setError("");

      await apiRequest(`/sales-orders/${id}/cancel`, {
        method: "POST",
        accessToken,
        body: { reason: reason.trim() },
      });

      await loadOrder();
    } catch (requestError) {
      setError(requestError.message || "Unable to cancel this sales order.");
    } finally {
      setIsActionLoading(false);
    }
  };

  const printOrder = () => window.print();

  const downloadOrder = () => {
    if (!order) return;

    const content = [
      `Sales Order: ${order.salesOrderNumber}`,
      `Customer: ${order.customerNameSnapshot}`,
      `Store: ${order.store?.name || ""}`,
      `Total: ${money(order.totalAmount)}`,
      "",
      "Items:",
      ...(order.items || []).map(
        (item) =>
          `${item.skuSnapshot} | ${item.productNameSnapshot} | Qty: ${item.quantityOrdered} | ${money(item.lineTotalAmount)}`,
      ),
    ].join("\n");

    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob([content], { type: "text/plain" }));
    link.download = `${order.salesOrderNumber}.txt`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-primary-100 border-t-primary-700 animate-spin" />
      </div>
    );
  }

  if (!order) {
    return <div className="rounded-2xl bg-red-50 p-6 text-red-700">{error}</div>;
  }

  const isDraft = order.status === "DRAFT";
  const canCancel = !["DELIVERED", "CANCELLED"].includes(order.status);

  const timeline = [
    {
      title: "Sales Order Created",
      user:
        order.createdBy?.employee?.fullName ||
        order.createdBy?.email ||
        "System",
      date: order.createdAt,
      color: "bg-green-500",
    },
    ...(order.confirmedAt
      ? [
          {
            title: "Order Confirmed — Stock Reserved",
            user:
              order.confirmedBy?.employee?.fullName ||
              order.confirmedBy?.email ||
              "System",
            date: order.confirmedAt,
            color: "bg-blue-500",
          },
        ]
      : []),
    ...(order.cancelledAt
      ? [
          {
            title: `Order Cancelled${order.cancellationReason ? ` — ${order.cancellationReason}` : ""}`,
            user: "System",
            date: order.cancelledAt,
            color: "bg-red-500",
          },
        ]
      : []),
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-start">
        <div>
          <button
            onClick={() => navigate("/admindashboard/sales/orders/list")}
            className="flex items-center gap-2 text-gray-500 hover:text-primary-700 mb-4"
          >
            <FiArrowLeft />
            Back to Sales Orders
          </button>

          <div className="flex flex-wrap items-center gap-4">
            <h1 className="text-4xl font-bold">{order.salesOrderNumber}</h1>
            <StatusBadge status={order.status} />
          </div>

          <p className="text-gray-500 mt-3">Sales Order Details</p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button onClick={printOrder} className="h-11 px-5 rounded-xl border border-gray-200 flex items-center gap-2">
            <FiPrinter />
            Print
          </button>

          <button onClick={downloadOrder} className="h-11 px-5 rounded-xl border border-gray-200 flex items-center gap-2">
            <FiDownload />
            Download
          </button>

          {isDraft && (
            <button
              disabled={isActionLoading}
              onClick={confirmOrder}
              className="h-11 px-6 rounded-xl bg-primary-700 text-white flex items-center gap-2 disabled:opacity-60"
            >
              <FiCheckCircle />
              Confirm Order
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid xl:grid-cols-2 gap-6">
        <InfoCard icon={<FiUser size={24} />} color="bg-primary-50 text-primary-700" title="Customer Information">
          <Info label="Customer" value={order.customerNameSnapshot} />
          <Info label="Phone" value={order.customerPhoneSnapshot} />
          <Info label="Email" value={order.customerEmailSnapshot} />
          <Info label="Billing Address" value={addressText(order.billingAddressSnapshot)} />
          <Info label="Shipping Address" value={addressText(order.shippingAddressSnapshot)} />
        </InfoCard>

        <InfoCard icon={<FiFileText size={24} />} color="bg-green-50 text-green-700" title="Order Information">
          <Info label="Order Number" value={order.salesOrderNumber} />
          <Info label="Order Date" value={dateOnly(order.orderDate)} />
          <Info label="Expected Delivery" value={dateOnly(order.expectedDate)} />
          <Info label="Created By" value={order.createdBy?.employee?.fullName || order.createdBy?.email} />
          <Info label="Store" value={`${order.store?.name || "—"}${order.store?.branch?.name ? ` · ${order.store.branch.name}` : ""}`} />
          <Info label="Reference" value={order.customerReference} />
        </InfoCard>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 p-8">
        <h2 className="text-2xl font-bold mb-8">Order Progress</h2>

        <div className="flex justify-between items-center overflow-x-auto">
          <ProgressStep title="Draft" completed />
          <Line completed={order.status !== "DRAFT"} />
          <ProgressStep title="Confirmed" completed={["CONFIRMED", "PARTIALLY_DELIVERED", "DELIVERED"].includes(order.status)} />
          <Line completed={["PARTIALLY_DELIVERED", "DELIVERED"].includes(order.status)} />
          <ProgressStep title="Delivery" completed={["PARTIALLY_DELIVERED", "DELIVERED"].includes(order.status)} />
          <Line completed={order.status === "DELIVERED"} />
          <ProgressStep title="Delivered" completed={order.status === "DELIVERED"} />
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
        <div className="flex overflow-x-auto border-b">
          {["overview", "products", "timeline"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-8 h-16 capitalize font-medium border-b-2 ${
                activeTab === tab
                  ? "border-primary-700 text-primary-700"
                  : "border-transparent text-gray-500"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {activeTab === "overview" && (
          <div className="p-6 space-y-6">
            <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
              <Metric label="Grand Total" value={money(order.totalAmount)} />
              <Metric label="Items" value={order.items?.length || 0} />
              <Metric label="Quantity" value={totalQuantity} />
              <Metric label="Status" value={statusLabels[order.status]} color="text-primary-700" />
            </div>

            <div className="grid xl:grid-cols-3 gap-6">
              <div className="xl:col-span-2 space-y-6">
                <div className="border border-gray-100 rounded-3xl p-6">
                  <h2 className="text-2xl font-bold mb-5">Customer Notes</h2>
                  <p className="text-gray-600 leading-7">
                    {order.customerNote || "No customer notes were added."}
                  </p>
                </div>

                <div className="border border-gray-100 rounded-3xl p-6">
                  <h2 className="text-2xl font-bold mb-5">Related Documents</h2>
                  <p className="text-gray-500">
                    Delivery challans, invoices, and payments will appear here once created.
                  </p>
                </div>
              </div>

              <div className="border border-gray-100 rounded-3xl p-6">
                <h2 className="text-xl font-bold">Order Summary</h2>
                <div className="space-y-5 mt-6">
                  <Info label="Subtotal" value={money(order.subtotalAmount)} />
                  <Info label="Discount" value={`- ${money(order.discountAmount)}`} />
                  <Info label="GST Included" value={money(order.taxAmount)} />
                </div>

                <div className="border-t my-6" />

                <div className="flex justify-between">
                  <span className="text-xl font-bold">Grand Total</span>
                  <span className="text-3xl font-bold text-primary-700">{money(order.totalAmount)}</span>
                </div>

                {canCancel && (
                  <button
                    disabled={isActionLoading}
                    onClick={cancelOrder}
                    className="w-full h-11 mt-7 rounded-xl border border-red-200 text-red-600 flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    <FiXCircle />
                    Cancel Order
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "products" && (
          <div className="overflow-x-auto p-6">
            <table className="w-full min-w-[900px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-5 py-4 text-left">Product</th>
                  <th className="px-5 py-4 text-left">Variant</th>
                  <th className="px-5 py-4 text-center">Ordered</th>
                  <th className="px-5 py-4 text-center">Delivered</th>
                  <th className="px-5 py-4 text-left">Lot Allocation</th>
                  <th className="px-5 py-4 text-right">Price</th>
                  <th className="px-5 py-4 text-center">GST</th>
                  <th className="px-5 py-4 text-right">Total</th>
                </tr>
              </thead>

              <tbody>
                {order.items?.map((item) => (
                  <tr key={item.id} className="border-b hover:bg-gray-50">
                    <td className="px-5 py-5">
                      <div className="flex items-center gap-4">
                        <div className="h-14 w-14 rounded-xl bg-gray-100 flex items-center justify-center">
                          <FiShoppingBag className="text-gray-400" size={22} />
                        </div>
                        <div>
                          <p className="font-semibold">{item.productNameSnapshot}</p>
                          <p className="text-xs text-gray-500 mt-1">SKU: {item.skuSnapshot}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-5">{item.variantNameSnapshot || "Default"}</td>
                    <td className="px-5 py-5 text-center font-semibold">{Number(item.quantityOrdered)}</td>
                    <td className="px-5 py-5 text-center">{Number(item.quantityDelivered)}</td>
                    <td className="px-5 py-5">
                      <LotAllocationSummary allocations={item.lotAllocations} />
                    </td>
                    <td className="px-5 py-5 text-right">{money(item.unitPrice)}</td>
                    <td className="px-5 py-5 text-center">{Number(item.taxRate)}%</td>
                    <td className="px-5 py-5 text-right font-bold">{money(item.lineTotalAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === "timeline" && (
          <div className="p-6 space-y-6">
            {timeline.map((item, index) => (
              <div key={`${item.title}-${index}`} className="flex gap-5">
                <div className="flex flex-col items-center">
                  <div className={`h-5 w-5 rounded-full ${item.color}`} />
                  {index < timeline.length - 1 && <div className="w-1 h-20 bg-gray-200" />}
                </div>

                <div className="flex-1 bg-gray-50 rounded-2xl p-5">
                  <p className="font-semibold text-lg">{item.title}</p>
                  <p className="text-gray-500 mt-2">{item.user}</p>
                  <p className="text-sm text-gray-400 mt-1">{dateTime(item.date)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function addressText(address) {
  if (!address || typeof address !== "object") return "—";

  return [
    address.recipient,
    address.addressLine1,
    address.addressLine2,
    address.city,
    address.state,
    address.postalCode,
    address.country,
  ]
    .filter(Boolean)
    .join(", ");
}

function InfoCard({ icon, color, title, children }) {
  return (
    <div className="bg-white rounded-3xl border border-gray-100 p-8">
      <div className="flex items-center gap-4 mb-8">
        <div className={`h-14 w-14 rounded-2xl flex items-center justify-center ${color}`}>
          {icon}
        </div>
        <div>
          <h2 className="text-2xl font-bold">{title}</h2>
          <p className="text-gray-500">Sales order details</p>
        </div>
      </div>
      {children}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="flex justify-between gap-6 py-4 border-b border-gray-100 last:border-b-0">
      <span className="text-gray-500">{label}</span>
      <span className="font-semibold text-right break-words">{value || "—"}</span>
    </div>
  );
}

function Metric({ label, value, color = "text-gray-900" }) {
  return (
    <div className="bg-gray-50 rounded-2xl p-5">
      <p className="text-gray-500 text-sm">{label}</p>
      <h2 className={`text-2xl font-bold mt-3 ${color}`}>{value}</h2>
    </div>
  );
}

function LotAllocationSummary({ allocations = [] }) {
  if (!allocations.length) {
    return <span className="text-sm text-gray-400">Legacy / not allocated</span>;
  }

  return (
    <div className="space-y-2 text-xs">
      {allocations.map((allocation) => {
        const lot = allocation.inventoryLot;
        const issued = Number(allocation.quantityIssued || 0);
        const released = Number(allocation.quantityReleased || 0);

        return (
          <div key={allocation.id} className="rounded-xl bg-gray-50 px-3 py-2">
            <p className="font-semibold text-gray-800">
              {lot?.lotNumber || "Lot unavailable"} · {Number(allocation.quantity)} unit(s)
            </p>
            <p className="mt-1 text-gray-500">
              {allocation.status.replace(/_/g, " ")} · Issued {issued} · Released {released}
            </p>
          </div>
        );
      })}
    </div>
  );
}

function ProgressStep({ title, completed = false }) {
  return (
    <div className="flex flex-col items-center min-w-[85px]">
      <div className={`h-14 w-14 rounded-2xl flex items-center justify-center font-bold ${completed ? "bg-green-500 text-white" : "bg-gray-100 text-gray-500"}`}>
        {completed ? <FiCheckCircle size={24} /> : title.charAt(0)}
      </div>
      <p className="mt-3 text-sm font-medium text-center">{title}</p>
    </div>
  );
}

function Line({ completed }) {
  return <div className={`h-1 flex-1 min-w-8 mx-3 ${completed ? "bg-green-500" : "bg-gray-200"}`} />;
}

function StatusBadge({ status }) {
  const colors = {
    DRAFT: "bg-gray-100 text-gray-700",
    CONFIRMED: "bg-blue-100 text-blue-700",
    PARTIALLY_DELIVERED: "bg-purple-100 text-purple-700",
    DELIVERED: "bg-green-100 text-green-700",
    CANCELLED: "bg-red-100 text-red-700",
  };

  return (
    <span className={`px-4 py-2 rounded-full font-semibold ${colors[status] || "bg-gray-100 text-gray-700"}`}>
      {statusLabels[status] || status}
    </span>
  );
}
